import { LoadingButton } from '@atlaskit/button';
import ModalDialog, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { useFlaggingCallback } from '@databases/flags';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { overrideModalBlanketStyles } from '../blanket';
import { useDialog } from '../index';
import { messages } from '../messages';

const ConfirmDialog: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState<1 | 2>();
  const { hideDialog, getData } = useDialog();
  const { heading, body, yesLabel, noLabel, appearance, onClose, isBlanketHidden } =
    getData<'ConfirmDialog'>();

  const handleClose = useFlaggingCallback(
    async (result: boolean) => {
      setIsLoading(result ? 1 : 2);
      await onClose(result);
      hideDialog();
    },
    [hideDialog, onClose]
  );

  const handleYesClick = useFlaggingCallback(async () => {
    await handleClose(true);
  }, [handleClose]);

  const handleNoClick = useFlaggingCallback(async () => {
    await handleClose(false);
  }, [handleClose]);

  const testId = 'confirm-dialog';

  useEffect(() => {
    overrideModalBlanketStyles(testId);
  }, []);

  const { formatMessage } = useIntl();
  const showNoLabel = !!noLabel || (!body && !yesLabel && !noLabel);

  return (
    <ModalDialog
      width="small"
      autoFocus
      onClose={handleNoClick}
      isBlanketHidden={isBlanketHidden}
      testId={testId}
    >
      <ModalHeader>
        <ModalTitle appearance={appearance ?? 'warning'}>
          {heading ?? formatMessage(messages['discard-changes-dialog.heading'])}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {body ?? formatMessage(messages['discard-changes-dialog.warning-body'])}
      </ModalBody>
      <ModalFooter>
        {showNoLabel && (
          <LoadingButton
            appearance="subtle"
            onClick={handleNoClick}
            isLoading={isLoading === 2}
            isDisabled={isLoading === 1}
          >
            {noLabel ?? formatMessage(messages['discard-changes-dialog.no-button-text'])}
          </LoadingButton>
        )}
        <LoadingButton
          appearance={appearance ?? 'primary'}
          type="submit"
          onClick={handleYesClick}
          isLoading={isLoading === 1}
          isDisabled={isLoading === 2}
        >
          {yesLabel ?? formatMessage(messages['discard-changes-dialog.yes-button-text'])}
        </LoadingButton>
      </ModalFooter>
    </ModalDialog>
  );
};

export default ConfirmDialog;
